import { useEffect, useState, useCallback } from 'react';
import { useDtpAuthStore } from '../store/useDtpAuthStore';
import { dtpApi } from '../lib/dtpApi';

export interface PatientProfile {
  sex: 'male' | 'female';
  age: number;
  weight: number;
  weightUnit: 'lbs' | 'kg';
  height: number;
  heightUnit: 'in' | 'cm';
}

const STORAGE_KEY = 'dtp.patientProfile.draft';

const DEFAULT_PROFILE: PatientProfile = {
  sex: 'male',
  age: 38,
  weight: 180,
  weightUnit: 'lbs',
  height: 70,
  heightUnit: 'in'
};

function ageFromDob(dob: Date | string): number {
  const d = typeof dob === 'string' ? new Date(dob) : dob;
  if (isNaN(d.getTime())) return DEFAULT_PROFILE.age;
  const diff = Date.now() - d.getTime();
  return Math.max(0, Math.floor(diff / (365.25 * 24 * 3600 * 1000)));
}

/**
 * Patient profile (sex/age/weight/height) used by the protocol builder.
 *
 * - When authenticated: seeds from Patient.screeningAnswers + dateOfBirth, and
 *   persists changes through PUT /api/dtp/screening so the membership and
 *   educational flows share one source of truth.
 * - When anonymous: keeps a draft in sessionStorage so a patient browsing the
 *   educational store doesn't re-enter their profile if they sign up later.
 */
export function usePatientProfile() {
  const { patient, isAuthenticated } = useDtpAuthStore();

  const [profile, setProfile] = useState<PatientProfile>(() => {
    if (typeof window !== 'undefined') {
      const draft = window.sessionStorage.getItem(STORAGE_KEY);
      if (draft) {
        try { return { ...DEFAULT_PROFILE, ...JSON.parse(draft) }; }
        catch { /* ignore */ }
      }
    }
    return DEFAULT_PROFILE;
  });
  const [saving, setSaving] = useState(false);

  // Seed from authenticated patient
  useEffect(() => {
    if (!isAuthenticated || !patient) return;
    const s = patient.screeningAnswers;
    setProfile(prev => ({
      sex: s?.biologicalSex || prev.sex,
      age: patient.dateOfBirth ? ageFromDob(patient.dateOfBirth) : prev.age,
      weight: s?.weightLbs || prev.weight,
      weightUnit: 'lbs',
      height: s?.heightInches || prev.height,
      heightUnit: 'in'
    }));
  }, [isAuthenticated, patient]);

  const update = useCallback((patch: Partial<PatientProfile>) => {
    setProfile(prev => {
      const next = { ...prev, ...patch };
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      }
      return next;
    });
  }, []);

  // Persist sex/weight/height to the patient record (if authenticated).
  // Uses the existing PUT /api/dtp/screening endpoint that already accepts
  // these fields. Age is derived from dateOfBirth which is set at signup
  // and not modifiable here.
  const persist = useCallback(async () => {
    if (!isAuthenticated || !patient) return;
    setSaving(true);
    try {
      const heightInches = profile.heightUnit === 'in'
        ? profile.height
        : Math.round(profile.height / 2.54);
      const weightLbs = profile.weightUnit === 'lbs'
        ? profile.weight
        : Math.round(profile.weight * 2.20462);
      const bmi = (weightLbs * 703) / (heightInches * heightInches);
      await dtpApi.updateScreening({
        // Spread existing answers we know about so we don't blow them away
        ...(patient.screeningAnswers || {}),
        biologicalSex: profile.sex,
        heightInches,
        weightLbs,
        bmi: Math.round(bmi * 10) / 10
      });
    } finally {
      setSaving(false);
    }
  }, [isAuthenticated, patient, profile]);

  return { profile, update, persist, saving, isAuthenticated };
}
